import { useCallback, useEffect } from 'react';
import { useAtom } from 'jotai';
import useAPI from '../../../api';
import { widgetPresetsAtom } from '../../../Atoms';
import {
  registerWidgetPresets,
  widgetPreview,
  widgetRenderer,
  widgetUsesEntries,
} from './WidgetRegistry';
import {
  DIALECTS,
  DIALECT_KEY,
  DIALECT_VERSIONS,
  readableAs,
} from '../dialect';

// Whether the server has been asked. Marked before the request, so the page rendering a layout and
// the editor offering the palette ask once between them, and cleared on a write so the next read
// fetches what was written.
let asked = false;

/**
 * A saved preset as the registry holds a widget, or nothing where what it holds cannot be read.
 *
 * What the row says of itself is taken as declared; the rest follows from the widget it names, since
 * a preset is drawn the way that widget is drawn.
 *
 * @param {Object} row - A preset row as the API returns it.
 *
 * @returns {Object|null}
 */
const declarationOf = (row) => {
  if (!row?.name) return null;

  const renderer = widgetRenderer(row.renderer || 'fields');

  try {
    const document = JSON.parse(row.jsonStructure || '{}');
    const { newer, from } = readableAs(
      DIALECTS.LAYOUT,
      Number(document[DIALECT_KEY])
    );

    // Offered as it stands rather than withheld, so a preset saved by a later build is still
    // placeable and says so once instead of quietly leaving the palette.
    if (newer)
      console.warn(
        `Widget preset ${row.id} (${row.name}) was saved in dialect ${from}, which this build does not know`
      );

    return {
      usesEntries: widgetUsesEntries(renderer),
      preview: widgetPreview(renderer),
      ...document,
      widget: row.name,
      label: row.label || row.name,
      renderer,
      // What the row is, so the editor can offer to replace or remove it and the registry knows
      // which of its entries came from one.
      presetId: row.id,
    };
  } catch {
    console.warn(`Widget preset ${row.id} (${row.name}) is not valid JSON`);
    return null;
  }
};

/**
 * What a region saved as a preset holds.
 *
 * A preset is the region without its place on the page: what it reads, the room it leaves and the
 * entries it starts with, all of which whatever places it may then override.
 *
 * @param {Object} region - The region being saved.
 *
 * @returns {Object}
 */
export const presetOfRegion = (region) => ({
  [DIALECT_KEY]: DIALECT_VERSIONS.layout,
  usesEntries: true,
  preview: 'live',
  preset: {
    ...(region?.source ? { source: region.source } : {}),
    ...(region?.padding ? { padding: region.padding } : {}),
    ...(region?.spacing !== undefined ? { spacing: region.spacing } : {}),
  },
  defaultEntries: region?.entries ?? [],
});

/**
 * The saved widget presets, in the registry and in hand.
 *
 * Fetched once for the session and registered as the widgets they are, so a layout naming one draws
 * it wherever it is read - the editor's palette offers the same set, since both read this. The
 * request is silent and its failure is not surfaced: a preset that cannot be read leaves the page
 * drawing whatever is checked in.
 *
 * @returns {Object} The rows, and the writes to them.
 */
const useWidgetPresets = () => {
  const API = useAPI();
  const [presets, setPresets] = useAtom(widgetPresetsAtom);

  useEffect(() => {
    if (presets || asked) return;

    asked = true;

    API.getWidgetPresets(
      { silent: true },
      (res) => setPresets(res ?? []),
      () => setPresets([])
    );
  }, [presets]);

  // In the registry rather than in a reader's hands: a layout names a preset the way it names any
  // widget, and the renderer looks it up where it looks the others up.
  useEffect(() => {
    if (!presets) return;

    registerWidgetPresets(presets.map(declarationOf).filter(Boolean));
  }, [presets]);

  /** Asks for the set again, for after one of them is written. */
  const reload = useCallback(() => {
    asked = false;
    setPresets(null);
  }, [setPresets]);

  const save = useCallback(
    (row, onSaved = () => {}) =>
      API.saveWidgetPreset(
        row,
        () => {
          reload();
          onSaved();
        },
        () => {}
      ),
    [reload]
  );

  const remove = useCallback(
    (id, onRemoved = () => {}) =>
      API.deleteWidgetPreset(
        id,
        () => {
          reload();
          onRemoved();
        },
        () => {}
      ),
    [reload]
  );

  return { presets: presets ?? [], save, remove };
};

export default useWidgetPresets;
